import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';

gsap.registerPlugin(Draggable);

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} handle
 * @param {number} position
 * @param {import('gsap/Draggable').Draggable | null} [draggable]
 */
function setSliderPosition(root, handle, position, draggable = null) {
	const pct = clamp(position, 0, 100);
	const width = root.offsetWidth;

	root.style.setProperty('--before-after-chart-position', `${pct}%`);
	handle.setAttribute('aria-valuenow', String(Math.round(pct)));

	if (!width) {
		return;
	}

	const x = (pct / 100) * width;
	gsap.set(handle, { x, xPercent: -50, left: 0, top: 0, y: 0 });

	if (draggable) {
		draggable.x = x;
		draggable.update(true);
	}
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} handle
 * @param {import('gsap/Draggable').Draggable} draggable
 */
function positionFromClientX(root, handle, clientX, draggable) {
	const rect = root.getBoundingClientRect();
	if (!rect.width) {
		return;
	}

	const pct = ((clientX - rect.left) / rect.width) * 100;
	setSliderPosition(root, handle, pct, draggable);
}

/**
 * @param {HTMLElement} root
 */
function initChartSlider(root) {
	if (root.dataset.chartSliderInit === 'true') {
		return;
	}

	const handle = root.querySelector('.before-after__chart-handle');
	if (!handle) {
		return;
	}

	root.dataset.chartSliderInit = 'true';

	const initialPosition = Number.parseFloat(root.dataset.initialPosition ?? '50');

	gsap.set(handle, { left: 0, top: 0, xPercent: -50, x: 0, y: 0 });

	const draggable = Draggable.create(handle, {
		type: 'x',
		bounds: root,
		trigger: root,
		cursor: 'ew-resize',
		activeCursor: 'grabbing',
		allowEventDefault: false,
		onPress() {
			root.classList.add('is-dragging');
		},
		onRelease() {
			root.classList.remove('is-dragging');
		},
		onDrag() {
			const width = root.offsetWidth;
			if (!width) {
				return;
			}

			const pct = (this.x / width) * 100;
			root.style.setProperty('--before-after-chart-position', `${clamp(pct, 0, 100)}%`);
			handle.setAttribute('aria-valuenow', String(Math.round(clamp(pct, 0, 100))));
		},
	})[0];

	setSliderPosition(
		root,
		handle,
		Number.isFinite(initialPosition) ? initialPosition : 50,
		draggable,
	);

	handle.addEventListener('keydown', (event) => {
		const step = event.shiftKey ? 10 : 2;
		const current = Number.parseFloat(
			root.style.getPropertyValue('--before-after-chart-position') || '50',
		);
		const base = Number.isFinite(current) ? current : 50;

		if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
			event.preventDefault();
			setSliderPosition(root, handle, base - step, draggable);
		}

		if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
			event.preventDefault();
			setSliderPosition(root, handle, base + step, draggable);
		}

		if (event.key === 'Home') {
			event.preventDefault();
			setSliderPosition(root, handle, 0, draggable);
		}

		if (event.key === 'End') {
			event.preventDefault();
			setSliderPosition(root, handle, 100, draggable);
		}
	});

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(() => {
			const current = Number.parseFloat(
				root.style.getPropertyValue('--before-after-chart-position') || '50',
			);
			setSliderPosition(
				root,
				handle,
				Number.isFinite(current) ? current : 50,
				draggable,
			);
		});

		observer.observe(root);
	}

	// Tap/click on track jumps handle without blocking Draggable press/drag.
	root.addEventListener('click', (event) => {
		if (!(event.target instanceof Element) || event.target.closest('.before-after__chart-handle')) {
			return;
		}

		if (draggable.isDragging || draggable.isPressed) {
			return;
		}

		positionFromClientX(root, handle, event.clientX, draggable);
	});
}

export function initBeforeAfterChartSliders() {
	document.querySelectorAll('.js-before-after-chart-slider').forEach((root) => {
		initChartSlider(root);
	});
}
